/**
 * Coupon Scanner Handler Functions
 * Business logic functions for Coupon Scanner component
 */

import {
    loadScannerWebsites,
    loadCouponScannerData,
    loadStoreHandle,
    loadMerchantInfo,
    updateCouponScanner,
    generateLinks,
    createMultipleCouponScanner
} from './couponScannerService';
import { handleStoreInfo, parseWebsiteUrls } from './helpers';
import { MESSAGES } from './constants';

/**
 * Handles input change for coupon scanner fields
 * Updates the appropriate state based on input name prefix
 * @param {Event} e - Input change event
 * @param {Function} setState - React setState function
 * @returns {void}
 */
export const handleCouponScannerInputChange = (e, setState) => {
    const { name, value } = e.target;
    const [key] = name.split('_');

    if (key === 'site') {
        setState(prevState => ({
            siteUrl: {
                ...prevState.siteUrl,
                [name]: value
            }
        }));
    } else if (key === 'website') {
        setState(prevState => ({
            websiteUrls: {
                ...prevState.websiteUrls,
                [name]: value
            }
        }));
    } else if (key === 'coupons') {
        setState(prevState => ({
            couponsData: {
                ...prevState.couponsData,
                [name]: value
            }
        }));
    }
};

/**
 * Processes scanner data and returns formatted state updates
 * @param {Array} scannerData - Array of scanner data objects
 * @returns {Object} Object with siteUrl, websiteUrls, couponsData, and newStore
 */
export const processScannerData = (scannerData) => {
    const siteUrlState = {};
    const websiteUrlsState = {};
    const couponsDataState = {};
    let newStore = '';

    scannerData.forEach(({ id, website_urls, site_url, coupons, store }) => {
        websiteUrlsState[`website_urls_${id}`] = website_urls?.join(',\n') || '';
        siteUrlState[`site_url_${id}`] = site_url || '';
        couponsDataState[`coupons_${id}`] = coupons || '';
        newStore = store || newStore;
    });

    return {
        siteUrl: siteUrlState,
        websiteUrls: websiteUrlsState,
        couponsData: couponsDataState,
        newStore
    };
};

/**
 * Loads coupon scanner data for a store
 * @param {string} store - Store name
 * @param {Function} notifyError - Error notification function
 * @param {Function} notifySuccess - Success notification function
 * @param {Function} notifyInfo - Info notification function
 * @returns {Promise<Object|null>} Object with loaded data or null if error
 */
export const loadCouponScannerDataHandler = async (store, notifyError, notifySuccess, notifyInfo) => {
    if (!store) return null;

    try {
        const [scannerWebsiteData, scannerData, storeHandleData, merchantInfoData] = await Promise.all([
            loadScannerWebsites(notifyError),
            loadCouponScannerData(store, notifyError),
            loadStoreHandle(store, notifyError),
            loadMerchantInfo(store, notifyError)
        ]);

        const store_info = handleStoreInfo(storeHandleData);

        // Process scanner data if available
        if (Array.isArray(scannerData) && scannerData.length > 0) {
            const processedData = processScannerData(scannerData);

            notifySuccess(MESSAGES.COUPON_SCANNER_LOADED_SUCCESS);

            return {
                ...processedData,
                couponScanner: scannerData,
                TotalCustomers: scannerData.length,
                id: storeHandleData?.id || '',
                domain: store_info?.domain || '',
                handle: store_info?.handle || '',
                query: store_info?.query || '',
                scannerWebsite: scannerWebsiteData || [],
                merchantInfo: merchantInfoData || {},
                loading: false
            };
        } else {
            // Empty or no data
            notifyInfo(MESSAGES.COUPON_SCANNER_NO_DATA);

            return {
                couponScanner: [],
                TotalCustomers: 0,
                scannerWebsite: scannerWebsiteData || [],
                merchantInfo: merchantInfoData || {},
                id: storeHandleData?.id || '',
                domain: store_info?.domain || '',
                handle: store_info?.handle || '',
                query: store_info?.query || '',
                loading: false
            };
        }
    } catch (err) {
        console.error(err);
        notifyError(MESSAGES.COUPON_SCANNER_LOAD_ERROR);
        return {
            couponScanner: [],
            loading: false
        };
    }
};

/**
 * Updates a coupon scanner entry
 * @param {string} id - Scanner website ID
 * @param {Object} state - Component state
 * @param {Function} notifyError - Error notification function
 * @param {Function} notifySuccess - Success notification function
 * @returns {Promise<boolean>} True if update successful, false otherwise
 */
export const updateCouponScannerHandler = async (id, state, notifyError, notifySuccess) => {
    const { store, newStore, couponsData, websiteUrls, siteUrl } = state;
    const storeName = newStore || store;

    const couponData = couponsData[`coupons_${id}`] || '';
    const websiteUrlsString = websiteUrls[`website_urls_${id}`] || '';
    const siteUrlValue = siteUrl[`site_url_${id}`] || '';

    // Convert websiteUrls to array using helper function
    const websiteUrlsArray = parseWebsiteUrls(websiteUrlsString);

    const result = await updateCouponScanner(
        {
            store: storeName,
            siteUrl: siteUrlValue,
            scannerWebsite: id,
            coupons: couponData,
            websiteUrls: websiteUrlsArray,
        },
        notifyError
    );

    if (result !== null) {
        notifySuccess(MESSAGES.COUPON_SCANNER_UPDATED_SUCCESS);
        return true;
    }

    return false;
};

/**
 * Transforms generated links into scanner coupon format
 * @param {Object} generatedLinks - Generated links data
 * @param {Array} scannerWebsite - Array of scanner website objects
 * @param {string} store - Store name
 * @returns {Array} Array of scanner coupon objects
 */
export const transformGeneratedLinks = (generatedLinks, scannerWebsite, store) => {
    if (!generatedLinks) return [];

    // Preprocess results into a map for fast lookup
    const resultsMap = new Map(scannerWebsite.map(item => [item.name, item]));
    const scanner_coupon = [];

    for (const [key, values] of Object.entries(generatedLinks)) {
        const result = resultsMap.get(key);
        if (result && Array.isArray(values) && values.length > 0) {
            scanner_coupon.push({
                store,
                scanner_website: result.id,
                website_urls: values,
                site_url: values[0] || ''
            });
        }
    }

    return scanner_coupon;
};

/**
 * Generates coupon scanner links
 * @param {Object} formData - Form data with domain, handle, query
 * @param {Object} state - Component state
 * @param {Function} notifyError - Error notification function
 * @param {Function} notifySuccess - Success notification function
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const generateLinksHandler = async (formData, state, notifyError, notifySuccess) => {
    const { domain, handle, query } = formData;
    const { store, scannerWebsite, id } = state;

    if (!domain || !handle || !query) {
        notifyError(MESSAGES.COUPON_SCANNER_LINKS_GENERATE_INVALID);
        return false;
    }

    try {
        // Generate links
        const generatedLinks = await generateLinks(
            domain,
            handle,
            query,
            notifyError
        );

        if (!generatedLinks) {
            return false;
        }

        // Transform generated links
        const scanner_coupon = transformGeneratedLinks(generatedLinks, scannerWebsite, store);

        // Create multiple coupon scanner entries
        if (scanner_coupon.length > 0) {
            await createMultipleCouponScanner(scanner_coupon, notifyError);
        }

        notifySuccess(MESSAGES.COUPON_SCANNER_LINKS_GENERATED_SUCCESS);
        return true;
    } catch (err) {
        console.error(`Error generating links: ${err}`);
        notifyError(MESSAGES.COUPON_SCANNER_LINKS_GENERATE_FAILED);
        return false;
    }
};

/**
 * Gets store from location object (supports React Router v5 and v6)
 * @param {Object} location - React Router location object
 * @returns {string} Store name or empty string
 */
export const getStoreFromLocation = (location) => {
    const stateData = location?.state?.data || location?.state || {};
    const storeFromState = stateData.store || location?.store;

    // Check URL search params as fallback
    const searchParams = new URLSearchParams(location?.search || '');
    const storeFromUrl = searchParams.get('store');

    // Use state first, then URL param
    return storeFromState || storeFromUrl || '';
};

